import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Iter47 — Multi-property (Chain) context.
 *
 * Loads the current partner's properties + chain metadata once on login, and
 * stores the selected `hotel_id` in localStorage so every subsequent API call
 * scopes to that property via the `X-Property-Id` header (see lib/api.js).
 *
 * Special value `"__all__"` means "aggregated view" — the switcher uses this
 * for the chain-wide dashboard. Component code should check `isAll` before
 * making single-hotel calls.
 */
const PropertyContext = createContext({
  properties: [],
  chain: null,
  selectedId: null,
  isAll: false,
  loading: true,
  refresh: async () => {},
  select: () => {},
});

export function PropertyProvider({ children }) {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [chain, setChain] = useState(null);
  const [selectedId, setSelectedId] = useState(() => localStorage.getItem("ss_selected_property") || null);
  const [loading, setLoading] = useState(true);

  const isPartner = !!user && (user.role === "hotel_partner" || user.role === "hotel_partner_pending");

  const refresh = useCallback(async () => {
    if (!isPartner) {
      setProperties([]);
      setChain(null);
      setSelectedId(null);
      localStorage.removeItem("ss_selected_property");
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        api.get("/my/properties").then((r) => r.data).catch(() => []),
        api.get("/my/chain").then((r) => r.data).catch(() => null),
      ]);
      setProperties(Array.isArray(p) ? p : []);
      setChain(c);
      // If the stored selection is stale (deleted / not owned), reset it.
      if (Array.isArray(p) && p.length > 0) {
        const stored = localStorage.getItem("ss_selected_property");
        const valid = stored === "__all__" || p.some((h) => h.hotel_id === stored);
        if (!valid) {
          const first = p[0].hotel_id;
          localStorage.setItem("ss_selected_property", first);
          setSelectedId(first);
        }
      } else if (Array.isArray(p) && p.length === 0) {
        localStorage.removeItem("ss_selected_property");
        setSelectedId(null);
      }
    } finally {
      setLoading(false);
    }
  }, [isPartner]);

  useEffect(() => { refresh(); }, [refresh]);

  const select = useCallback((hotelId) => {
    if (!hotelId) {
      localStorage.removeItem("ss_selected_property");
      setSelectedId(null);
      return;
    }
    localStorage.setItem("ss_selected_property", hotelId);
    setSelectedId(hotelId);
  }, []);

  const isAll = selectedId === "__all__";

  return (
    <PropertyContext.Provider value={{ properties, chain, selectedId, isAll, loading, refresh, select }}>
      {children}
    </PropertyContext.Provider>
  );
}

export const useProperty = () => useContext(PropertyContext);
